import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { CircleCheck, Clock, AlertTriangle, TrendingUp, Activity as ActivityIcon } from "lucide-react";
import { Avatar, ProgressBar, STATUS_META, PRIORITY_META } from "../components/ui-bits";
import { formatDistanceToNow } from "date-fns";

const CHART_TICK = { fontSize: 11 };
const CHART_TICK_Y = { fontSize: 11 };
const BAR_RADIUS = [4, 4, 0, 0];

export default function Dashboard() {
  const { setOpenTaskId, spaces } = useData();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const statusData = useMemo(() => (!stats ? [] : Object.entries(stats.by_status).map(([k, v]) => ({ name: STATUS_META[k].label, value: v, color: STATUS_META[k].color }))), [stats]);
  const priorityData = useMemo(() => (!stats ? [] : Object.entries(stats.by_priority).map(([k, v]) => ({ name: PRIORITY_META[k].label, value: v, color: PRIORITY_META[k].color }))), [stats]);
  const cards = useMemo(() => (!stats ? [] : [
    { label: "Total tasks", value: stats.total, icon: TrendingUp, color: "#1B2A6B", testid: "stat-total" },
    { label: "In progress", value: stats.by_status.in_progress, icon: Clock, color: "#3B82F6", testid: "stat-in-progress" },
    { label: "Completed", value: stats.done, icon: CircleCheck, color: "#10B981", testid: "stat-done" },
    { label: "Overdue", value: stats.overdue, icon: AlertTriangle, color: "#E31E24", testid: "stat-overdue" },
  ]), [stats]);

  if (!stats) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="dashboard-loading">
        <div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="dashboard-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(({ label, value, icon: Icon, color, testid }) => (
          <div key={testid} data-testid={testid} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">{label}</p>
              <span className="h-7 w-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}18`, color }}>
                <Icon size={15} />
              </span>
            </div>
            <p className="font-heading text-2xl font-extrabold mt-1" data-testid={`${testid}-value`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4" data-testid="chart-status">
          <h3 className="font-heading text-base font-semibold mb-2">Tasks by status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={statusData} dataKey="value" innerRadius={50} outerRadius={75} paddingAngle={3} strokeWidth={0}>
                {statusData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center">
            {statusData.map((d) => (
              <span key={d.name} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.color }} />{d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4" data-testid="chart-priority">
          <h3 className="font-heading text-base font-semibold mb-2">Tasks by priority</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={priorityData}>
              <XAxis dataKey="name" tick={CHART_TICK} />
              <YAxis allowDecimals={false} tick={CHART_TICK_Y} width={24} />
              <Tooltip />
              <Bar dataKey="value" radius={BAR_RADIUS}>
                {priorityData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      <div data-testid="spaces-overview">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base font-semibold">All projects by space</h3>
          <span className="text-xs font-bold text-[#10B981]" data-testid="completion-pct">{stats.completion_pct}% overall completion</span>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {spaces.map((space) => {
            const projs = stats.per_project.filter((p) => p.space_id === space.id);
            return (
              <div key={space.id} data-testid={`space-card-${space.id}`} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                  <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: space.color }} />
                  <span className="font-heading font-semibold text-sm truncate">{space.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">{projs.length} project{projs.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-3">
                  {projs.length === 0 && <p className="text-xs text-muted-foreground">No projects in this space yet.</p>}
                  {projs.map((p) => (
                    <button key={p.id} data-testid={`space-project-${p.id}`} onClick={() => navigate(`/project/${p.id}`)} className="w-full text-left group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium group-hover:text-[#E31E24] transition-colors truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0 ml-2">{p.done}/{p.total} · {p.pct}%</span>
                      </div>
                      <ProgressBar value={p.pct} />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {spaces.length === 0 && <p className="text-sm text-muted-foreground">No spaces yet. Create one from the sidebar.</p>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4" data-testid="recent-activity">
        <h3 className="font-heading text-base font-semibold mb-3 flex items-center gap-2"><ActivityIcon size={15} />Recent activity</h3>
        <div className="space-y-2.5">
          {stats.recent_activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          {stats.recent_activity.map((a) => (
            <button key={a.id} data-testid={`activity-item-${a.id}`} onClick={() => a.task_id && setOpenTaskId(a.task_id)}
              className="w-full flex gap-2.5 text-sm text-left hover:bg-muted/60 rounded-md p-1 -m-1 transition-colors">
              <Avatar name={a.user_name || "?"} userId={a.user_id} size="sm" className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="truncate"><span className="font-semibold">{a.user_name}</span> <span className="text-muted-foreground">{a.detail}</span></p>
                <p className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
